import { Lead, LeadStatus, LeadSource, LeadType, IntentType } from "../interfaces/DTO/crm/lead";

export const formatLeadName = (lead: Pick<Lead, "first_name" | "last_name">): string =>
  `${lead.first_name} ${lead.last_name}`.trim();

export const getLeadStatusColor = (status: LeadStatus): string => {
  const map: Record<LeadStatus, string> = {
    [LeadStatus.NEW]: "#0288d1",
    [LeadStatus.CONTACTED]: "#1976d2",
    [LeadStatus.QUALIFIED]: "#7b1fa2",
    [LeadStatus.IN_FLOW]: "#66bb6a",
    [LeadStatus.DEAL_CREATED]: "#2e7d32",
    [LeadStatus.CONVERTED]: "#388e3c",
    [LeadStatus.LOST]: "#757575",
    [LeadStatus.INVALID_DETAILS]: "#b71c1c",
    [LeadStatus.NEEDS_CONTACT]: "#e65100",
  };
  return map[status] ?? "#9e9e9e";
};

export const formatLeadStatus = (status: LeadStatus): string => {
  const map: Record<LeadStatus, string> = {
    [LeadStatus.NEW]: "חדש",
    [LeadStatus.CONTACTED]: "בוצע קשר",
    [LeadStatus.QUALIFIED]: "מתאים לשירות",
    [LeadStatus.IN_FLOW]: "בתהליך",
    [LeadStatus.DEAL_CREATED]: "נוצרה עסקה",
    [LeadStatus.CONVERTED]: "עסקה עם נתוני מסלקה",
    [LeadStatus.LOST]: "לא מעוניין",
    [LeadStatus.INVALID_DETAILS]: "פרטים שגויים",
    [LeadStatus.NEEDS_CONTACT]: "ליצור קשר",
  };
  return map[status] || status;
};

export const formatLeadSource = (source: LeadSource): string => {
  const map: Record<LeadSource, string> = {
    [LeadSource.PHONE]: "טלפון",
    [LeadSource.WEBSITE]: "אתר",
    [LeadSource.WHATSAPP]: "וואטסאפ",
    [LeadSource.REFERRAL]: "הפניה",
    [LeadSource.WEBINAR]: "וובינר",
    [LeadSource.MANUAL]: "ידני",
  };
  return map[source] || source;
};


export const formatLeadType = (type: LeadType): string => {
  const map: Record<LeadType, string> = {
    [LeadType.CUSTOMER]: "לקוח",
    [LeadType.CUSTOMER_PARTNER]: "בן/בת זוג",
  };
  return map[type] || type;
};

export const colorLeadType = (type: LeadType): string => {
  const map: Record<LeadType, string> = {
    [LeadType.CUSTOMER]: "#4CAF82",
    [LeadType.CUSTOMER_PARTNER]: "#E57FAA",
  };
  return map[type] ?? "#9c27b0";
};

export interface WhatsAppMessageParams {
  lead: Pick<Lead, "first_name" | "lead_type" | "intent_type">;
  campaignName?: string;
  companyName?: string;
  portalLink?: string;
  senderName?: string;
  customerFirstName?: string;
}

export const buildWhatsAppMessage = ({
  lead,
  campaignName = "[שם קמפיין]",
  companyName = "[שם חברה]",
  portalLink = "[LEADPORTALLINK]",
  senderName = "[שמך]",
  customerFirstName = "[שם הלקוח]",
}: WhatsAppMessageParams): string => {
  if (!lead.lead_type || lead.lead_type === LeadType.CUSTOMER) {
    const coupleText = lead.intent_type === IntentType.COUPLE_CONSULTATION ? "לך ולבן זוגך" : "שלך";
    return `הי ${lead.first_name},

נעים מאוד, ${senderName} מ-${companyName}

שמחנו לראות שהצטרפת לתהליך התכנון הפיננסי במסגרת ${campaignName}

כדי שנוכל להתקדם לשלב הבא, מצרפת כאן שוב את הקישור להשלמת ההרשמה:

${portalLink}

לאחר השלמת התהליך נוכל להתחיל בהכנת התמונה הפיננסית ${coupleText}
לכל שאלה אנחנו כאן.`;
  }
  if (lead.lead_type === LeadType.CUSTOMER_PARTNER) {
    return `היי ${lead.first_name},

אני ${senderName} מ-${companyName}

${customerFirstName} צירף/ה אותך לתהליך התכנון הפיננסי במסגרת ${campaignName}

כדי שנוכל להתחיל בתהליך ולהכין עבורכם תמונה פיננסית מלאה, נשמח לקבל גם את השלמת ההרשמה שלך כאן:

${portalLink}

לאחר השלמת ההרשמה נוכל להתחיל בתהליך עבורכם

לכל שאלה אנחנו כאן.

יום נעים,
${senderName}`;
  }
  return "";
};

export interface WhatsAppMessageToCustomerAboutPartnerParams {
  customer: Pick<Lead, "first_name">;
  partnerFirstName?: string;
  campaignName?: string;
  companyName?: string;
  partnerPortalLink?: string;
  senderName?: string;
}

export const buildWhatsAppMessageToCustomerAboutPartner = ({
  customer,
  partnerFirstName = "[שם בן/בת הזוג]",
  campaignName = "[שם קמפיין]",
  companyName = "[שם חברה]",
  partnerPortalLink = "[LEADPORTALLINK]",
  senderName = "[שמך]",
}: WhatsAppMessageToCustomerAboutPartnerParams): string => {
  return `היי ${customer.first_name},

אני ${senderName} מ-${companyName}

שמחנו לראות שהשלמת את החלק שלך בתהליך התכנון הפיננסי במסגרת ${campaignName}

כדי שנוכל להתקדם לשלב הבא, נשמח לקבל גם את השלמת ההרשמה של ${partnerFirstName} כאן:

${partnerPortalLink}

לאחר מכן נוכל להתחיל בהכנת התמונה הפיננסית עבורכם

לכל שאלה אנחנו כאן.

יום נעים,
${senderName}`;
};

export const formatIntentType = (intent: IntentType): string => {
  const map: Record<IntentType, string> = {
    [IntentType.ONE_TIME_CONSULTATION]: "ייעוץ חד-פעמי",
    [IntentType.COUPLE_CONSULTATION]: "ייעוץ זוגי",
    [IntentType.ONGOING]: "ליווי מתמשך",
    [IntentType.UNKNOWN]: "לא ידוע",
  };
  return map[intent] || intent;
};
